import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DangerZone from "@/components/DangerZone";
import {
  CameraIcon, ImageIcon, UserCircleIcon, InstagramLogoIcon,
  TiktokLogoIcon, XLogoIcon, StorefrontIcon, ArrowLeftIcon,
} from "@phosphor-icons/react";

const MAX_BIO = 200;

export default function ProfileEdit() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const avatarInput = useRef(null);
  const coverInput = useRef(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isSeller = useMemo(
    () => profile?.account_type?.endsWith("_seller"),
    [profile]
  );

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        setProfile(data);
        setForm({
          name: data.name || "",
          username: data.username || "",
          bio: data.bio || "",
          email: data.email || "",
          instagram: data.social_links?.instagram || "",
          tiktok: data.social_links?.tiktok || "",
          x: data.social_links?.x || "",
          shop_name: data.shop_name || "",
          shop_tagline: data.shop_tagline || "",
          stream_schedule: data.stream_schedule || "",
          return_policy: data.return_policy || "",
          shipping_regions: data.shipping_regions || "",
        });
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, [user, loading, navigate]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 grid place-items-center text-zinc-500">Loading…</div>
        <Footer />
      </div>
    );
  }

  const cooldownLeft = profile.username_cooldown_days_left || 0;

  const upload = async (file, endpoint, label) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`${label} must be under 8MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(data);
      refreshUser?.();
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
        email: form.email,
        social_links: {
          instagram: form.instagram,
          tiktok: form.tiktok,
          x: form.x,
        },
      };
      // Only send username if the user actually changed it
      if ((form.username || "").toLowerCase() !== (profile.username || "").toLowerCase()) {
        payload.username = form.username;
      }
      if (isSeller) {
        payload.shop_name = form.shop_name;
        payload.shop_tagline = form.shop_tagline;
        payload.stream_schedule = form.stream_schedule;
        payload.return_policy = form.return_policy;
        payload.shipping_regions = form.shipping_regions;
      }
      const { data } = await api.patch("/me/profile", payload);
      setProfile(data);
      refreshUser?.();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const bioLeft = MAX_BIO - (form.bio || "").length;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="profile-edit-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4"
          data-testid="profile-back"
        >
          <ArrowLeftIcon size={14} /> Back
        </button>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
          Edit profile
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Control how you show up on streams, listings, and your public page.
        </p>

        {/* Cover + avatar */}
        <div className="mt-6 rounded-2xl overflow-hidden border border-zinc-200">
          <div className="relative h-40 sm:h-56 bg-gradient-to-br from-[#1D78AB] via-[#165d85] to-zinc-900">
            {profile.cover_image && (
              <img
                src={profile.cover_image}
                alt="cover"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="profile-cover-preview"
              />
            )}
            <button
              type="button"
              onClick={() => coverInput.current?.click()}
              className="absolute right-3 top-3 rounded-full bg-black/60 hover:bg-black/80 text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 backdrop-blur"
              data-testid="profile-cover-upload-btn"
            >
              <ImageIcon size={14} /> {profile.cover_image ? "Replace cover" : "Add cover"}
            </button>
            <input
              ref={coverInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0], "/me/cover", "Cover image")}
              data-testid="profile-cover-input"
            />
          </div>
          <div className="relative px-5 pb-5 -mt-12">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="relative">
                <div className="h-24 w-24 rounded-full border-4 border-white bg-zinc-100 grid place-items-center overflow-hidden shadow">
                  {profile.picture ? (
                    <img
                      src={profile.picture}
                      alt={profile.name}
                      className="h-full w-full object-cover"
                      data-testid="profile-avatar-preview"
                    />
                  ) : (
                    <UserCircleIcon size={64} className="text-zinc-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => avatarInput.current?.click()}
                  className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white grid place-items-center shadow-lg"
                  data-testid="profile-avatar-upload-btn"
                  aria-label="Upload profile picture"
                >
                  <CameraIcon size={16} weight="fill" />
                </button>
                <input
                  ref={avatarInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0], "/me/picture", "Profile picture")}
                  data-testid="profile-avatar-input"
                />
              </div>
              <div className="pt-4">
                {/* Visible to OTHERS — show the public handle here so users
                    immediately see what other buyers/sellers will see. */}
                <div className="font-display font-black text-xl">
                  {profile.username ? `@${profile.username}` : profile.name}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {profile.username
                    ? `Public handle · others see @${profile.username}`
                    : "Set a username so other users can find you"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Identity */}
        <Section title="Identity">
          <Field
            label="Username"
            hint={
              cooldownLeft > 0
                ? `You can change this again in ${cooldownLeft} day${cooldownLeft === 1 ? "" : "s"}.`
                : "Public handle — what other users see on bids, listings, and streams. Lowercase letters, numbers, and underscores. Changeable once every 30 days."
            }
            testid="profile-username"
          >
            <div className="flex items-center gap-2">
              <span className="text-zinc-500">@</span>
              <Input
                value={form.username}
                onChange={update("username")}
                placeholder="your_handle"
                maxLength={20}
                disabled={cooldownLeft > 0}
                data-testid="profile-username-input"
              />
            </div>
          </Field>
          <Field
            label="Full Name (private)"
            hint="Your real legal name — only visible to you and Bidorah staff. Used for ID verification, payouts, and shipping. Never shown to other users."
            testid="profile-name"
          >
            <Input value={form.name} onChange={update("name")} maxLength={60} data-testid="profile-name-input" />
          </Field>
          <Field
            label="Bio"
            hint={`${bioLeft} characters left`}
            testid="profile-bio"
          >
            <Textarea
              value={form.bio}
              onChange={update("bio")}
              maxLength={MAX_BIO}
              rows={3}
              placeholder="A short intro — what you sell, why people should tune in."
              data-testid="profile-bio-input"
            />
          </Field>
          <Field label="Email" testid="profile-email">
            <Input type="email" value={form.email} onChange={update("email")} data-testid="profile-email-input" />
          </Field>
        </Section>

        {/* Socials */}
        <Section title="Social links">
          <Field label="Instagram" icon={<InstagramLogoIcon size={18} />} testid="profile-social-ig">
            <Input value={form.instagram} onChange={update("instagram")} placeholder="@username (without @)" data-testid="profile-ig-input" />
          </Field>
          <Field label="TikTok" icon={<TiktokLogoIcon size={18} />} testid="profile-social-tt">
            <Input value={form.tiktok} onChange={update("tiktok")} placeholder="@username (without @)" data-testid="profile-tt-input" />
          </Field>
          <Field label="X / Twitter" icon={<XLogoIcon size={18} />} testid="profile-social-x">
            <Input value={form.x} onChange={update("x")} placeholder="@username (without @)" data-testid="profile-x-input" />
          </Field>
        </Section>

        {/* Seller-only */}
        {isSeller && (
          <Section title="Shop details" icon={<StorefrontIcon size={18} className="text-[#1D78AB]" />}>
            <Field label="Shop name" testid="profile-shop-name">
              <Input value={form.shop_name} onChange={update("shop_name")} maxLength={60} data-testid="profile-shopname-input" />
            </Field>
            <Field label="Shop tagline" hint="One-line pitch shown on stream thumbnails." testid="profile-shop-tagline">
              <Input value={form.shop_tagline} onChange={update("shop_tagline")} maxLength={120} data-testid="profile-tagline-input" />
            </Field>
            <Field label="Stream schedule" hint={"e.g. 'Live every Thu 8pm PT'"} testid="profile-stream-schedule">
              <Input value={form.stream_schedule} onChange={update("stream_schedule")} maxLength={120} data-testid="profile-schedule-input" />
            </Field>
            <Field label="Return policy" testid="profile-return-policy">
              <Textarea value={form.return_policy} onChange={update("return_policy")} maxLength={500} rows={3} data-testid="profile-returns-input" />
            </Field>
            <Field label="Shipping regions" hint="Where you ship — e.g. 'US only' or 'US, CA, MX'" testid="profile-shipping">
              <Input value={form.shipping_regions} onChange={update("shipping_regions")} maxLength={200} data-testid="profile-shipping-input" />
            </Field>
          </Section>
        )}

        <div className="sticky bottom-4 mt-8 flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="profile-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || uploading}
            className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
            data-testid="profile-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <DangerZone />
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="mt-8 border border-zinc-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="font-display font-bold text-lg">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, hint, icon, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      {children}
      {hint && <div className="text-xs text-zinc-500 mt-1">{hint}</div>}
    </label>
  );
}
